import {DeviceType} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {
    GlobalEventsMapForBackwardCompatibility,
    UIElementRepositionData,
    UIElementsSwapLayerEvent
} from '@gongxigames/framework/slot/events';
import {KeypadControlsView} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsView';
import {IKeypadControlsView} from '@gongxigames/framework/slot/modules/keypadControls/IKeypadControlsView';
import {KeypadControlsViewMobileLandscape} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsViewMobileLandscape';
import {KeypadControlsViewMobilePortrait} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsViewMobilePortrait';
import {ScreenOrientedView} from '@gongxigames/framework/slot/fragments/module/view';

export class KeypadControlsViewMobile extends ScreenOrientedView<GlobalEventsMapForBackwardCompatibility, KeypadControlsView> implements IKeypadControlsView {
    public static readonly NAME: symbol = Symbol('KeypadControlsViewMobile');
    public static layer = LAYERS.KEYPAD;
    public static platform: DeviceType = DeviceType.MOBILE;

    protected landscapeViewName: symbol = KeypadControlsViewMobileLandscape.NAME;
    protected portraitViewName: symbol = KeypadControlsViewMobilePortrait.NAME;

    private _isBetControlsOpened = false;
    private _disabledTurboCheckbox = false;

    public hideBetPanel(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.hideBetPanel();
        });
    }

    public showBetPanel(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.showBetPanel();
        });
    }

    public disableForeverAutoplayButton(): void {}

    public disableForeverTurboCheckbox(): void {
        this._disabledTurboCheckbox = true;

        this._views.forEach((view: KeypadControlsView) => {
            view.disableForeverTurboCheckbox();
        });
    }

    public playTransitionToFreeSpins(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.playTransitionToFreeSpins();
        });
    }

    public playTransitionToMainGame(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.playTransitionToMainGame();
        });
    }

    public enableAutoplayButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableAutoplayButton();
        });
    }

    public disableAutoplayButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableAutoplayButton();
        });
    }

    public enableInfoButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableInfoButton();
        });
    }

    public enableTurboCheckbox(): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._views.forEach((view: KeypadControlsView) => {
            view.enableTurboCheckbox();
        });
    }

    public enableBetButtons(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableBetButtons();
        });
    }

    public disableInfoButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableInfoButton();
        });
    }

    public disableTurboCheckbox(): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._views.forEach((view: KeypadControlsView) => {
            view.disableTurboCheckbox();
        });
    }

    public disableBetButtons(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableBetButtons();
        });
    }

    public enableBetIncButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableBetIncButton();
        });
    }

    public disableBetIncButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableBetIncButton();
        });
    }

    public enableBetDecButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableBetDecButton();
        });
    }

    public disableBetDecButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableBetDecButton();
        });
    }

    public enableMenuButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableMenuButton();
        });
    }

    public disableMenuButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableMenuButton();
        });
    }

    public enableTotalBetOpenButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableTotalBetOpenButton();
        });
    }

    public disableTotalBetOpenButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableTotalBetOpenButton();
        });

        this.closeTotalBetWindow();
    }

    public hideTotalBetOpenButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.hideTotalBetOpenButton();
        });

        this.closeTotalBetWindow();
    }

    public showTotalBetOpenButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.showTotalBetOpenButton();
        });

        this.closeTotalBetWindow();
    }

    public swapUIElementsLayer(data: UIElementsSwapLayerEvent): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.swapUIElementsLayer(data);
        });
    }

    public repositionElements(data: UIElementRepositionData[]): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.repositionElements(data);
        });
    }

    public enableHomeButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.enableHomeButton();
        });
    }

    public disableHomeButton(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.disableHomeButton();
        });
    }

    public setBetValueText(text: string): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.setBetValueText(text);
        });
    }

    public openTotalBetWindow(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.openTotalBetWindow();
        });

        this._isBetControlsOpened = true;
    }

    public closeTotalBetWindow(): void {
        this._views.forEach((view: KeypadControlsView) => {
            view.closeTotalBetWindow();
        });

        this._isBetControlsOpened = false;
    }

    public updateTurboCheckbox(isOn: boolean): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._views.forEach((view: KeypadControlsView) => {
            view.updateTurboCheckbox(isOn);
        });
    }

    public get isBetControlsOpened(): boolean {
        return this._isBetControlsOpened;
    }

    public set isBetControlsOpened(value: boolean) {
        this._isBetControlsOpened = value;
    }
}
